import _ from 'lodash';
import { getSlug } from '../services/localize';
import {
    DIGITAL_MARKETING_CATEGORY_ID,
    PROGRAMMING_AND_TECH_CATEGORY_ID,
    GRAPHICS_AND_DESIGN_CATEGORY_ID,
    WRITING_AND_TRANSLATION_CATEGORY_ID,
    BUSINESS_CATEGORY_ID
} from '../constants';

const API = {
    FETCH_NAVIGATION_CONFIG: '/categories/navigation_config',
    FETCH_SIDEBAR_CONFIG: 'sidebar_navigation_config'
};

const TOGGLE_DELAY = {
    SHORT: 40,
    LONG: 180,
    EXTRA_LONG: 600
};

const TOGGLE_DELAY_THRESHOLD = 2;

const TYPES = {
    CATEGORY: 'category',
    PRO_CATEGORY: 'pro_category',
    SUB_CATEGORY: 'sub_category',
    PRO_SUB_CATEGORY: 'pro_sub_category',
    STORE: 'store',
    SEARCH_QUERY: 'search_query',
    PRO_SEARCH_QUERY: 'pro_search_query',
    NESTED_SUB_CATEGORY: 'nested_sub_category',
    STATIC_PAGE: 'static_page',
    FILTERED_PAGE: 'filtered_page',
    FILTERED_SUB_CATEGORY: 'filtered_sub_category',
    PLACEHOLDER: 'placeholder',
    MENU_PANEL: 'menu_panel',
    STATIC: 'static',
    LOGO_MAKER_IDEAS: 'ideas',
    CONTENT_PAGE: 'content_page',
    LANDING_PAGE: 'landing_page',
    LINK_URL: 'link_url'
};

const FILTER_SEPARATORS = {
    PARAMS: '|',
    PARAM_VALUE: ':',
    ARRAY_VALUE: ','
};

const DEFAULT_QUERY_PARAMS = {
    source: 'category_tree'
};

const PATHFINDER = {
    STORE: 'store',
    SEARCH: 'search_gigs',
    CATEGORY: 'short_category',
    PRO_CATEGORY: 'short_pro_category',
    PRO_SUB_CATEGORY: 'short_pro_sub_category',
    SUB_CATEGORY: 'short_category_sub_category',
    NESTED_SUB_CATEGORY: 'short_category_nested_sub_category',
    SUB_CATEGORY_STATIC_PAGE: 'sub_category_static_page',
    NESTED_SUB_CATEGORY_STATIC_PAGE: 'nested_sub_category_static_page',
    LOGO_MAKER_IDEAS_PAGE: 'logo_maker_buyer_ideas_page',
    CONTENT_PAGE: 'content_landing_page',
    LANDING_PAGE: 'static_page',
    CONTENT_PAGES_PAGE: 'content_pages_page'
};

const MODES = {
    DEFAULT: 'default',
    PRO: 'pro'
};

const PATHS = {
    ROOT: '#!'
};

const BADGES = {
    NEW: 'new',
    BETA: 'beta'
};

const MENU_PANEL_TYPE = {
    DEFAULT: 'default',
    BUCKET: 'bucket',
    BUCKETS_WITH_TITLE: 'buckets_with_title',
    BUCKETS_WITHOUT_TITLE: 'buckets_without_title'
};

const MENU_TYPES_CLASS_MAPPER = {
    default: 'default',
    bucket: 'bucket',
    menu_panel: 'menu-panel',
    buckets_with_title: 'buckets-with-title',
    buckets_without_title: 'buckets-without-title'
};

const _stringifyActiveFilterValue = (value) => Array.isArray(value) ? value.join(FILTER_SEPARATORS.ARRAY_VALUE) : value;

const defaultI18n = { t: _.identity };

const filtersToQueryString = (filters) =>
    Object.entries(filters || {})
        .map(([filterName, filterValue]) =>
            `${filterName}${FILTER_SEPARATORS.PARAM_VALUE}${_stringifyActiveFilterValue(filterValue)}`
        )
        .join(FILTER_SEPARATORS.PARAMS);

const formatQueryParams = (queryConfig = {}) => {
    const { filter, queryParams = {}, source } = queryConfig;
    const defaultQueryParams = { ...DEFAULT_QUERY_PARAMS, ...(source && { source }) };

    // temporary support in both data structures
    if (_.isString(queryParams)) {
        return {
            query: {
                ...defaultQueryParams,
                ref: queryParams
            }
        };
    }

    if (!filter) {
        return { query: { ...defaultQueryParams, ...queryParams } };
    }

    return {
        query: {
            ...defaultQueryParams,
            ...queryParams,
            ref: filtersToQueryString(filter)
        }
    };
};

const removeEmptyEntries = (params = {}) => {
    Object.keys(params).forEach((key) => {
        const value = params[key];
        (value === null || value === undefined || value === '') && delete params[key];
        (Array.isArray(value) && value.length === 0) && delete params[key];
    });

    return params;
};

const getCategorySlug = (id, locale) => getSlug(`categories.category_${id}.cached_slug`, locale);
const getSubCategorySlug = (id, locale) => getSlug(`sub_categories.sub_category_${id}.cached_slug`, locale);
const getNestedSubCategorySlug = (id, subCategoryId, locale) => getSlug(`sub_categories.sub_category_${subCategoryId}.nested_sub_cat_${id}.cached_slug`, locale);

const OVERFLOW_OFFSET_PADDING = 20;
const FULLSCREEN_PANEL_SCREEN_SIZE = 1200;

const overflowHandler = (menuPanelRef, setOverflowStylingFunc, isBriefButtonOnLeftVariation) => {
    const windowWidth = window.innerWidth;
    const menuPanelWidth = menuPanelRef.current.getBoundingClientRect().width;

    const MIN_LEFT_OFFSET = -160;

    if (windowWidth <= FULLSCREEN_PANEL_SCREEN_SIZE || menuPanelWidth >= windowWidth) {
        const baseLeft = isBriefButtonOnLeftVariation ? MIN_LEFT_OFFSET : 0;

        setOverflowStylingFunc({ left: baseLeft, right: 0 });

        if (menuPanelRef.current?.classList.contains('four-columns-menu')) {
            menuPanelRef.current.style.minWidth = '1070px';
        }

        return;
    }

    const paddedWindowWidth = windowWidth - OVERFLOW_OFFSET_PADDING;
    const parentLiEl = menuPanelRef.current.parentElement;
    const liBoundingRectLeft = parentLiEl.getBoundingClientRect().left;
    const overflowAmount = paddedWindowWidth - menuPanelWidth - liBoundingRectLeft;
    const relativeContainer = document.querySelector('#categories-menu-package');
    const relContainerLeft = relativeContainer.getBoundingClientRect().left;

    const liLeft = liBoundingRectLeft - relContainerLeft;

    let adjustedLeft = liLeft;

    if (overflowAmount < 0) {
        adjustedLeft += overflowAmount;
    }

    if (isBriefButtonOnLeftVariation) {
        adjustedLeft = Math.max(adjustedLeft - 160, MIN_LEFT_OFFSET);
    } else {
        adjustedLeft = Math.max(adjustedLeft, 0);
    }

    setOverflowStylingFunc({ left: adjustedLeft });
};

export const getAgenciesDescriptionKey = (categoryId) => {
    switch (categoryId) {
        case DIGITAL_MARKETING_CATEGORY_ID:
            return 'digital_marketing';
        case PROGRAMMING_AND_TECH_CATEGORY_ID:
            return 'programming_and_tech';
        case GRAPHICS_AND_DESIGN_CATEGORY_ID:
            return 'graphics_and_design';
        case WRITING_AND_TRANSLATION_CATEGORY_ID:
            return 'writing_and_translation';
        case BUSINESS_CATEGORY_ID:
            return 'business';
        default:
            break;
    }
};

export {
    TYPES,
    MENU_TYPES_CLASS_MAPPER,
    PATHFINDER,
    MODES,
    PATHS,
    BADGES,
    MENU_PANEL_TYPE,
    formatQueryParams,
    filtersToQueryString,
    getCategorySlug,
    getSubCategorySlug,
    getNestedSubCategorySlug,
    removeEmptyEntries,
    overflowHandler,
    defaultI18n,
    FILTER_SEPARATORS,
    TOGGLE_DELAY,
    TOGGLE_DELAY_THRESHOLD,
    API
};
